
export default function Footer() {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setSubmitMessage('Please enter your email address');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d4eo1maokpliv29boao0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Thank you for subscribing!');
        setEmail('');
      } else {
        setSubmitMessage('There was an error. Please try again.');
      }
    } catch (error) {
      console.error('Newsletter submission error:', error);
      setSubmitMessage('There was an error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-gray-900 text-white py-12">
      {/* Newsletter Section */}
      <div className="bg-teal-600 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-2xl font-bold mb-4">Stay Informed on AI Ethics</h3>
            <p className="mb-6">
              Get insights, speaking updates, and thought leadership delivered to your inbox
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-readdy-form>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-white text-sm"
                required
                disabled={isSubmitting}
              />
              <button
                type="submit"
                className="bg-gray-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
            {submitMessage && (
              <p className={`mt-3 text-sm ${submitMessage.includes('Thank you') ? 'text-white' : 'text-red-200'}`}>
                {submitMessage}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8">
          
          {/* Brand */}
          <div className="space-y-4">
            <h3 className="text-2xl font-bold">ALBERTO ROCHA</h3>
            <p className="text-gray-400">
              Author of "AI Is More Human Than Humans" and expert on the Consistency Paradox in artificial intelligence and human decision-making.
            </p>
            <div className="flex space-x-4">
              <a href="#" className="w-8 h-8 bg-gray-700 hover:bg-teal-600 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                <i className="ri-linkedin-fill text-sm"></i>
              </a>
              <a href="#" className="w-8 h-8 bg-gray-700 hover:bg-teal-600 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                <i className="ri-twitter-x-fill text-sm"></i>
              </a>
              <a href="#" className="w-8 h-8 bg-gray-700 hover:bg-teal-600 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                <i className="ri-youtube-fill text-sm"></i>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold">Quick Links</h4>
            <div className="space-y-2">
              <button onClick={() => scrollToSection('book')} className="block text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
                The Book
              </button>
              <button onClick={() => scrollToSection('bio')} className="block text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
                About Alberto
              </button>
              <button onClick={() => scrollToSection('speaking')} className="block text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
                Speaking
              </button>
              <button onClick={() => scrollToSection('platform')} className="block text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
                Media
              </button>
            </div>
          </div>

          {/* Services */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold">Services</h4>
            <div className="space-y-2">
              <p className="text-gray-400">Keynote Speaking</p>
              <p className="text-gray-400">Corporate Workshops</p>
              <p className="text-gray-400">Media Interviews</p>
              <p className="text-gray-400">Research Collaboration</p>
              <p className="text-gray-400">AI Ethics Consulting</p>
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold">Contact</h4>
            <div className="space-y-2">
              <p className="text-gray-400">
                <i className="ri-mail-line mr-2"></i>
                hello@aiismorehumanthanhumans.com
              </p>
              <p className="text-gray-400">
                <i className="ri-presentation-line mr-2"></i>
                speaking@aiismorehumanthanhumans.com
              </p>
              <p className="text-gray-400">
                <i className="ri-mic-line mr-2"></i>
                media@aiismorehumanthanhumans.com
              </p>
            </div>
          </div>

        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-400 text-sm">
            © 2024 Alberto Rocha. All rights reserved.
          </p>
          <div className="flex items-center space-x-4 mt-4 md:mt-0">
            <a href="#" className="text-gray-400 hover:text-teal-400 text-sm transition-colors cursor-pointer">
              Privacy Policy
            </a>
            <a href="#" className="text-gray-400 hover:text-teal-400 text-sm transition-colors cursor-pointer">
              Terms of Service
            </a>
            <a href="https://readdy.ai/?origin=logo" className="text-gray-400 hover:text-teal-400 text-sm transition-colors cursor-pointer">
              Website Builder
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
